import React, { useEffect, useRef } from "react";
import "./responsive.css";

/**
 * ResponsiveScope — drop-in mobile/tablet responsive layer.
 *
 * Wraps a desktop-first, inline-styled page and makes it usable on phones and
 * small tablets WITHOUT touching the page's own markup or logic. Two parts:
 *
 *   1. responsive.css — media-query overrides scoped under `.qv-responsive`.
 *      Desktop (>900px) is untouched; nothing leaks outside this wrapper.
 *   2. The runtime adapter below — on narrow viewports only, it tags DOM nodes
 *      with helper classes / data-attributes so the CSS can target them:
 *        - wide grid "tables" -> stacked, labelled cards (data-mwcard/-mwlabel)
 *        - overflowing toolbars -> wrap, with the search/control full width
 *        - fixed multi-up KPI grids -> 2-up
 *        - squeezed side-by-side banners -> stacked
 *      All tags are classes/attributes (never inline style) so React re-renders
 *      don't wipe them; a MutationObserver re-applies after navigation. The
 *      adapter is completely inert on desktop.
 *
 * Mark any already-responsive subtree with `data-mwskip` to exclude it.
 */

const MOBILE_QUERY = "(max-width: 768px)";

function tracks(gtc) {
  return gtc.split(/\s+(?![^(]*\))/).filter(Boolean);
}

function tagTables(root) {
  const els = root.querySelectorAll('[style*="grid-template-columns"]');
  const candidates = [];
  for (let i = 0; i < els.length; i++) {
    const el = els[i];
    if (el.closest("[data-mwskip]")) continue;
    if (el.hasAttribute("data-mwcard") || el.classList.contains("mw-thead")) continue;
    const gtc = el.style && el.style.gridTemplateColumns;
    if (!gtc) continue;
    const t = tracks(gtc);
    const pxFixed = (gtc.match(/\d+px/g) || []).length;
    const isKpi =
      /^repeat\(\s*([3-9]|1[0-2])\s*,/.test(gtc) ||
      (t.length >= 3 && t.length <= 6 && t.every((x) => /fr$/.test(x)));
    if (isKpi) {
      el.classList.add("mw-kpi");
      continue;
    }
    if (t.length >= 3 || pxFixed >= 2) candidates.push({ el, gtc });
  }

  const groups = {};
  candidates.forEach((c) => {
    (groups[c.gtc] = groups[c.gtc] || []).push(c.el);
  });

  function headerScore(row) {
    const kids = [].slice.call(row.children);
    const textCells = kids.filter(
      (k) => k.textContent.trim().length > 0 && !k.querySelector("input, button")
    );
    if (textCells.length < 2) return 0;
    const rowUpper = getComputedStyle(row).textTransform === "uppercase";
    let up = 0;
    let veryLong = false;
    textCells.forEach((k) => {
      const cs = getComputedStyle(k);
      const txt = k.textContent.trim();
      const isUpper =
        cs.textTransform === "uppercase" ||
        rowUpper ||
        (txt === txt.toUpperCase() && /[A-Z]/.test(txt));
      if (isUpper) up++;
      if (txt.length > 40) veryLong = true;
    });
    return !veryLong && up >= Math.ceil(textCells.length * 0.6) ? 2 : 0;
  }

  Object.keys(groups).forEach((gtc) => {
    const rows = groups[gtc];
    const header = rows.length >= 2 && headerScore(rows[0]) >= 2 ? rows[0] : null;
    let labels = null;
    if (header) {
      labels = [].slice.call(header.children).map((k) => k.textContent.trim());
      header.classList.add("mw-thead");
    }
    rows.forEach((row) => {
      if (row === header) return;
      row.setAttribute("data-mwcard", "1");
      if (labels) {
        const kids = [].slice.call(row.children);
        for (let k = 0; k < kids.length; k++) {
          const lab = labels[k] || "";
          if (lab && !kids[k].hasAttribute("data-mwlabel")) {
            kids[k].setAttribute("data-mwlabel", lab);
          } else if (!lab) {
            kids[k].setAttribute("data-mwlabel", "");
          }
        }
      }
    });
  });
}

function tagCardStrips(root) {
  root.querySelectorAll("*").forEach((el) => {
    if (el.closest("[data-mwskip]")) return;
    const cs = getComputedStyle(el);
    if (cs.display !== "flex" || cs.flexDirection === "column" || cs.flexWrap === "wrap") return;
    if (el.classList.contains("mw-cards") || el.classList.contains("mw-scroll")) return;
    const kids = [].slice.call(el.children);
    if (kids.length < 3 || kids.length > 6) return;
    const allCards = kids.every((k) => {
      const ks = getComputedStyle(k);
      return (
        ks.borderTopLeftRadius !== "0px" &&
        (ks.borderStyle !== "none" || ks.boxShadow !== "none") &&
        parseFloat(ks.flexGrow) >= 1
      );
    });
    if (!allCards) return;
    const narrow = kids.some((k) => k.getBoundingClientRect().width < 130);
    if (narrow) el.classList.add("mw-cards");
  });
}

function tagHeaders(root) {
  const W = window.innerWidth;
  root.querySelectorAll("*").forEach((el) => {
    const cs = getComputedStyle(el);
    if (cs.display !== "flex" || cs.flexDirection === "column") return;
    if (cs.flexWrap === "wrap") return;
    if (el.closest("[data-mwskip]")) return;
    if (
      el.classList.contains("mw-scroll") ||
      el.classList.contains("mw-row") ||
      el.classList.contains("mw-cards") ||
      el.classList.contains("mw-xscroll") ||
      el.hasAttribute("data-mwcard")
    )
      return;
    const tooWide = el.scrollWidth > W + 2;
    if (!tooWide) return;
    const hasInput = !!el.querySelector("input");
    if (hasInput) {
      el.classList.add("mw-wrap");
      let box = el.querySelector("input");
      const inp = box;
      for (let k = 0; k < 3 && box && box !== el; k++) {
        if (box.parentElement === el) break;
        box = box.parentElement;
      }
      (box || inp).classList.add("mw-fluid");
    } else if (el.querySelector("button")) {
      const bkids = [].slice.call(el.children);
      const bigText = bkids.some((k) => {
        const ks = getComputedStyle(k);
        return parseFloat(ks.fontSize) >= 17 || k.textContent.trim().length > 20;
      });
      if (bkids.length <= 3 || bigText) el.classList.add("mw-wrap");
      else el.classList.add("mw-xscroll");
    } else {
      el.classList.add("mw-wrap");
    }
  });
}

function tagBanners(root) {
  root.querySelectorAll("*").forEach((el) => {
    if (el.closest("[data-mwskip]")) return;
    const cs = getComputedStyle(el);
    if (cs.display !== "flex" || cs.flexDirection === "column" || cs.flexWrap === "wrap") return;
    if (
      el.classList.contains("mw-cards") ||
      el.classList.contains("mw-wrap") ||
      el.classList.contains("mw-xscroll") ||
      el.classList.contains("mw-stack")
    )
      return;
    const kids = [].slice.call(el.children);
    if (kids.length < 2 || kids.length > 3) return;
    const lens = kids.map((k) => k.textContent.trim().length);
    const maxLen = Math.max.apply(null, lens);
    const bigKid = kids[lens.indexOf(maxLen)];
    if (maxLen > 60 && bigKid.getBoundingClientRect().width < 210) {
      el.classList.add("mw-stack");
      bigKid.classList.add("mw-stack-fill");
    }
  });
}

export default function ResponsiveScope({ children, className = "", style, fullBleed = false }) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root || typeof window === "undefined" || !window.matchMedia) return undefined;

    const mq = window.matchMedia(MOBILE_QUERY);
    const isMobile = () => mq.matches;

    const apply = () => {
      if (!isMobile()) return;
      try {
        tagTables(root);
        tagCardStrips(root);
        tagHeaders(root);
        tagBanners(root);
      } catch (e) {
        /* never let layout tagging break the page */
      }
    };

    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      window.requestAnimationFrame(() => {
        pending = false;
        apply();
      });
    };

    apply();

    let observer;
    try {
      observer = new MutationObserver(schedule);
      observer.observe(root, { childList: true, subtree: true });
    } catch (e) {
      observer = null;
    }
    window.addEventListener("resize", schedule, { passive: true });
    const timers = [120, 350, 800, 1500].map((t) => setTimeout(apply, t));

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", schedule);
      timers.forEach(clearTimeout);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`qv-responsive${fullBleed ? " qv-inventory-workspace" : ""} ${className}`.trim()}
      data-inv-fullbleed={fullBleed ? "1" : undefined}
      style={style}
    >
      {children}
    </div>
  );
}
